import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { CalendarDays, Clock, Coffee, Cigarette, User, Download, FileSpreadsheet, Info, AlertTriangle, CheckCircle, Undo2, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ExternalLink } from 'lucide-react';
import { GET_TIME_REPORT, GET_ALL_EMPLOYEES, GET_WORK_ANOMALIES, ACKNOWLEDGE_ANOMALY, REVOKE_ANOMALY_ACKNOWLEDGEMENT } from '../../graphql/queries';
import { EXPORT_WORK_TIME_REPORT, EXPORT_DAILY_WORK_REPORT, EXPORT_SHIFT_REPORT } from '../../graphql/mutations';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tooltip } from '../ui/tooltip';
import { MultiSelect } from '../ui/multi-select';
import { usePageSize } from '../../hooks/usePageSize';
import { usePermission } from '../../hooks/usePermission';

interface LeaveDetail {
  leaveType: string;
  displayName: string;
  color: string;
  days: number;
}

interface TimeReportEntry {
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    department?: string;
    position?: string;
  };
  workHours: number;
  breakHours: number;
  smokeHours: number;
  overtime50: number;
  overtime100: number;
  totalEvents: number;
  leaveDays: number;
  leaveDetails: LeaveDetail[];
  startDate: string;
  endDate: string;
}

interface AnomalyAcknowledgement {
  id: string;
  employeeId: string;
  date: string;
  anomalyType: string;
  acknowledgedBy: string;
  acknowledgedByEmail: string;
  note?: string;
  createdAt: string;
}

interface WorkAnomaly {
  type: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH';
  message: string;
  date: string;
  timestamp?: string;
  acknowledged?: boolean;
  acknowledgement?: AnomalyAcknowledgement;
}

interface AnomalyReport {
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    department?: string;
    position?: string;
  };
  anomalies: WorkAnomaly[];
  totalAnomalies: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
}

interface ReportsProps {
  onNavigateToEvents?: (employeeId: string, startDate: string, endDate: string, e?: React.MouseEvent) => void;
  currentUserRole?: string;
}

export const Reports: React.FC<ReportsProps> = ({ onNavigateToEvents, currentUserRole }) => {
  const canRevokeAck = usePermission('reports.revokeAcknowledgement');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportPeriod, setReportPeriod] = useState<'current' | 'previous' | 'custom'>('current');
  const [exportMessage, setExportMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [applyTolerances, setApplyTolerances] = useState(true);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = React.useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'report' | 'anomalies'>('report');
  const [currentPage, setCurrentPage] = useState(1);
  const { pageSize: PAGE_SIZE, setPageSize: savePageSize } = usePageSize();
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [anomalyTypeFilter, setAnomalyTypeFilter] = useState<string>('all');
  const [showAcknowledged, setShowAcknowledged] = useState(false);
  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [ackTarget, setAckTarget] = useState<{ employeeId: string; date: string; type: string; employeeName: string } | null>(null);
  const [ackNote, setAckNote] = useState('');
  const [selectedAnomalies, setSelectedAnomalies] = useState<Set<number>>(new Set());
  const [bulkAckDialogOpen, setBulkAckDialogOpen] = useState(false);
  const [bulkAckNote, setBulkAckNote] = useState('');
  const [bulkAckLoading, setBulkAckLoading] = useState(false);
  const [acknowledgeAnomaly] = useMutation(ACKNOWLEDGE_ANOMALY);
  const [revokeAcknowledgement] = useMutation(REVOKE_ANOMALY_ACKNOWLEDGEMENT);
  const [exportReport, { loading: exportLoading }] = useMutation(EXPORT_WORK_TIME_REPORT);
  const [exportDailyReport, { loading: exportDailyLoading }] = useMutation(EXPORT_DAILY_WORK_REPORT);
  const [exportShiftReport, { loading: exportShiftLoading }] = useMutation(EXPORT_SHIFT_REPORT);

  const { data: employeesData } = useQuery(GET_ALL_EMPLOYEES, {
    fetchPolicy: "network-only",
  });

  // Set default dates on mount - load from sessionStorage if available
  useEffect(() => {
    const savedStartDate = sessionStorage.getItem('reports_startDate');
    const savedEndDate = sessionStorage.getItem('reports_endDate');
    const savedPeriod = sessionStorage.getItem('reports_period') as 'current' | 'previous' | 'custom' | null;
    const savedEmployeeIds = sessionStorage.getItem('reports_employeeIds');
    const savedDepartments = sessionStorage.getItem('reports_departments');
    const savedApplyTolerances = sessionStorage.getItem('reports_applyTolerances');

    if (savedStartDate && savedEndDate && savedPeriod) {
      setStartDate(savedStartDate);
      setEndDate(savedEndDate);
      setReportPeriod(savedPeriod);
      if (savedEmployeeIds) {
        try { setSelectedEmployeeIds(JSON.parse(savedEmployeeIds)); } catch {}
      }
      if (savedDepartments) {
        try { setSelectedDepartments(JSON.parse(savedDepartments)); } catch {}
      }
      if (savedApplyTolerances !== null) {
        setApplyTolerances(savedApplyTolerances === 'true');
      }
    } else {
      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const startDateStr = currentMonthStart.toISOString().split('T')[0];
      const endDateStr = currentMonthEnd.toISOString().split('T')[0];

      setStartDate(startDateStr);
      setEndDate(endDateStr);

      sessionStorage.setItem('reports_startDate', startDateStr);
      sessionStorage.setItem('reports_endDate', endDateStr);
      sessionStorage.setItem('reports_period', 'current');
      sessionStorage.setItem('reports_employeeIds', '[]');
      sessionStorage.setItem('reports_departments', '[]');
      sessionStorage.setItem('reports_applyTolerances', 'true');
    }
  }, []);

  // Save dates and filters to sessionStorage whenever they change
  useEffect(() => {
    if (startDate && endDate) {
      sessionStorage.setItem('reports_startDate', startDate);
      sessionStorage.setItem('reports_endDate', endDate);
      sessionStorage.setItem('reports_period', reportPeriod);
      sessionStorage.setItem('reports_employeeIds', JSON.stringify(selectedEmployeeIds));
      sessionStorage.setItem('reports_departments', JSON.stringify(selectedDepartments));
      sessionStorage.setItem('reports_applyTolerances', String(applyTolerances));
    }
  }, [startDate, endDate, reportPeriod, selectedEmployeeIds, selectedDepartments, applyTolerances]);


  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target as Node)) {
        setExportDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data, loading, error, refetch } = useQuery(GET_TIME_REPORT, {
    variables: {
      startDate: startDate ? new Date(startDate + 'T00:00:00.000Z') : new Date(),
      endDate: endDate ? new Date(endDate + 'T23:59:59.999Z') : new Date(),
      employeeIds: selectedEmployeeIds.length > 0 ? selectedEmployeeIds : undefined,
      applyTolerances: applyTolerances,
    },
    skip: !startDate || !endDate,
    fetchPolicy: "network-only",
  });

  const { data: anomaliesData, loading: anomaliesLoading, refetch: refetchAnomalies } = useQuery(GET_WORK_ANOMALIES, {
    variables: {
      startDate: startDate ? new Date(startDate + 'T00:00:00.000Z') : new Date(),
      endDate: endDate ? new Date(endDate + 'T23:59:59.999Z') : new Date(),
      employeeIds: selectedEmployeeIds.length > 0 ? selectedEmployeeIds : undefined,
      showAcknowledged,
    },
    skip: !startDate || !endDate,
    fetchPolicy: "network-only",
  });

  const handlePeriodChange = (period: 'current' | 'previous' | 'custom') => {
    setReportPeriod(period);

    const now = new Date();

    if (period === 'current') {
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(currentMonthStart.toISOString().split('T')[0]);
      setEndDate(currentMonthEnd.toISOString().split('T')[0]);
    } else if (period === 'previous') {
      const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      setStartDate(previousMonthStart.toISOString().split('T')[0]);
      setEndDate(previousMonthEnd.toISOString().split('T')[0]);
    }
  };

  const handleRefresh = () => {
    if (startDate && endDate) {
      refetch({
        startDate: new Date(startDate + 'T00:00:00.000Z'),
        endDate: new Date(endDate + 'T23:59:59.999Z'),
        employeeIds: selectedEmployeeIds.length > 0 ? selectedEmployeeIds : undefined,
        applyTolerances: applyTolerances,
      });
    }
  };

  const formatHours = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getTotalHours = (entries: TimeReportEntry[], type: 'work' | 'break' | 'smoke') => {
    return entries.reduce((total, entry) => {
      switch (type) {
        case 'work': return total + entry.workHours;
        case 'break': return total + entry.breakHours;
        case 'smoke': return total + entry.smokeHours;
        default: return total;
      }
    }, 0);
  };

  const handleEmployeeClick = (employeeId: string, e: React.MouseEvent) => {
    if (onNavigateToEvents) {
      onNavigateToEvents(employeeId, startDate, endDate, e);
    }
  };

  const handleExportToExcel = async () => {
    try {
      const { data } = await exportReport({
        variables: {
          startDate: new Date(startDate + 'T00:00:00.000Z'),
          endDate: new Date(endDate + 'T23:59:59.999Z'),
          employeeIds: selectedEmployeeIds.length > 0 ? selectedEmployeeIds : undefined,
          applyTolerances: applyTolerances,
          departments: selectedDepartments.length > 0 ? selectedDepartments : undefined,
        },
      });

      if (data?.exportWorkTimeReportToExcel) {
        const base64Data = data.exportWorkTimeReportToExcel;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `raport_${startDate}_${endDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setExportMessage({ type: 'success', text: 'Raport został wyeksportowany do pliku Excel' });
        setTimeout(() => setExportMessage(null), 5000);
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportMessage({ type: 'error', text: 'Nie udało się wyeksportować raportu' });
      setTimeout(() => setExportMessage(null), 5000);
    }
  };

  const handleExportDailyToExcel = async () => {
    try {
      const { data } = await exportDailyReport({
        variables: {
          startDate: new Date(startDate + 'T00:00:00.000Z'),
          endDate: new Date(endDate + 'T23:59:59.999Z'),
          employeeIds: selectedEmployeeIds.length > 0 ? selectedEmployeeIds : undefined,
          applyTolerances: applyTolerances,
          departments: selectedDepartments.length > 0 ? selectedDepartments : undefined,
        },
      });

      if (data?.exportDailyWorkReportToExcel) {
        const base64Data = data.exportDailyWorkReportToExcel;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `kadry_${startDate}_${endDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setExportMessage({ type: 'success', text: 'Raport dzienny został wyeksportowany do pliku Excel' });
        setTimeout(() => setExportMessage(null), 5000);
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportMessage({ type: 'error', text: 'Nie udało się wyeksportować raportu' });
      setTimeout(() => setExportMessage(null), 5000);
    }
  };

  const handleExportShiftToExcel = async () => {
    try {
      const { data } = await exportShiftReport({
        variables: {
          startDate: new Date(startDate + 'T00:00:00.000Z'),
          endDate: new Date(endDate + 'T23:59:59.999Z'),
          employeeIds: selectedEmployeeIds.length > 0 ? selectedEmployeeIds : undefined,
          applyTolerances: applyTolerances,
          departments: selectedDepartments.length > 0 ? selectedDepartments : undefined,
        },
      });

      if (data?.exportShiftReportToExcel) {
        const base64Data = data.exportShiftReportToExcel;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `raport_zmianowy_${startDate}_${endDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setExportMessage({ type: 'success', text: 'Raport zmianowy został wyeksportowany do pliku Excel' });
        setTimeout(() => setExportMessage(null), 5000);
      }
    } catch (error) {
      console.error('Export error:', error);
      setExportMessage({ type: 'error', text: 'Nie udało się wyeksportować raportu zmianowego' });
      setTimeout(() => setExportMessage(null), 5000);
    }
  };

  // Filter report data by selected department (client-side filtering)
  const reportData = React.useMemo(() => {
    const rawData = data?.timeReport || [];
    if (selectedDepartments.length === 0) {
      return rawData;
    }
    return rawData.filter((entry: any) => selectedDepartments.includes(entry.employee.department));
  }, [data?.timeReport, selectedDepartments]);

  // Reset page when data or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [reportData.length, selectedDepartments, selectedEmployeeIds]);

  // Get unique departments from employees
  const departments = React.useMemo(() => {
    if (!employeesData?.employees) return [];
    const deptSet = new Set<string>();
    employeesData.employees.forEach((emp: any) => {
      if (emp.department) {
        deptSet.add(emp.department);
      }
    });
    return Array.from(deptSet).sort((a, b) => a.localeCompare(b));
  }, [employeesData]);

  // Filter employees by selected department
  const filteredEmployees = React.useMemo(() => {
    if (!employeesData?.employees) return [];
    let employees = employeesData.employees;
    if (selectedDepartments.length > 0) {
      employees = employees.filter((emp: any) => selectedDepartments.includes(emp.department));
    }
    return employees.slice().sort((a: any, b: any) => a.lastName.localeCompare(b.lastName));
  }, [employeesData, selectedDepartments]);

  // Reset employee selection when department changes and selected employees are not in filtered list
  useEffect(() => {
    if (selectedEmployeeIds.length > 0 && selectedDepartments.length > 0) {
      const filteredIds = new Set(filteredEmployees.map((emp: any) => emp.id));
      const validIds = selectedEmployeeIds.filter(id => filteredIds.has(id));
      if (validIds.length !== selectedEmployeeIds.length) {
        setSelectedEmployeeIds(validIds);
      }
    }
  }, [selectedDepartments, filteredEmployees, selectedEmployeeIds]);

  // Flatten anomalies for the table, apply filters
  const flatAnomalies = React.useMemo(() => {
    const reports: AnomalyReport[] = anomaliesData?.workAnomalies || [];
    const rows: Array<WorkAnomaly & { employeeName: string; employeeDepartment?: string; employeeId: string }> = [];

    for (const report of reports) {
      // Filter by department
      if (selectedDepartments.length > 0 && (!report.employee.department || !selectedDepartments.includes(report.employee.department))) continue;

      for (const anomaly of report.anomalies) {
        if (severityFilter !== 'all' && anomaly.severity !== severityFilter) continue;
        if (anomalyTypeFilter !== 'all' && anomaly.type !== anomalyTypeFilter) continue;
        rows.push({
          ...anomaly,
          employeeId: report.employee.id,
          employeeName: `${report.employee.lastName} ${report.employee.firstName}`,
          employeeDepartment: report.employee.department || undefined,
        });
      }
    }

    // Sort by date descending, then severity
    const severityOrder: Record<string, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };
    rows.sort((a, b) => {
      const dateCmp = b.date.localeCompare(a.date);
      if (dateCmp !== 0) return dateCmp;
      return (severityOrder[a.severity] || 2) - (severityOrder[b.severity] || 2);
    });

    return rows;
  }, [anomaliesData, selectedDepartments, severityFilter, anomalyTypeFilter]);

  // Get unique anomaly types for filter
  const anomalyTypes = React.useMemo(() => {
    const reports: AnomalyReport[] = anomaliesData?.workAnomalies || [];
    const types = new Set<string>();
    for (const report of reports) {
      for (const a of report.anomalies) {
        types.add(a.type);
      }
    }
    return Array.from(types).sort();
  }, [anomaliesData]);

  // Per-employee anomaly counts for the report table
  const anomalyByEmployee = React.useMemo(() => {
    const reports: AnomalyReport[] = anomaliesData?.workAnomalies || [];
    const map = new Map<string, { total: number; high: number; medium: number; low: number }>();
    for (const r of reports) {
      map.set(r.employee.id, {
        total: r.totalAnomalies,
        high: r.highCount,
        medium: r.mediumCount,
        low: r.lowCount,
      });
    }
    return map;
  }, [anomaliesData]);

  // Total anomaly count for badge
  const totalAnomalyCount = React.useMemo(() => {
    const reports: AnomalyReport[] = anomaliesData?.workAnomalies || [];
    return reports.reduce((sum, r) => sum + r.totalAnomalies, 0);
  }, [anomaliesData]);

  const highAnomalyCount = React.useMemo(() => {
    const reports: AnomalyReport[] = anomaliesData?.workAnomalies || [];
    return reports.reduce((sum, r) => sum + r.highCount, 0);
  }, [anomaliesData]);

  const mediumAnomalyCount = React.useMemo(() => {
    const reports: AnomalyReport[] = anomaliesData?.workAnomalies || [];
    return reports.reduce((sum, r) => sum + r.mediumCount, 0);
  }, [anomaliesData]);

  const anomalyEmployeeCount = React.useMemo(() => {
    const reports: AnomalyReport[] = anomaliesData?.workAnomalies || [];
    return reports.length;
  }, [anomaliesData]);

  const severityBadge = (severity: string) => {
    switch (severity) {
      case 'HIGH':
        return <Badge variant="destructive">Wysoki</Badge>;
      case 'MEDIUM':
        return <Badge className="bg-yellow-500 text-white hover:bg-yellow-600">Średni</Badge>;
      case 'LOW':
        return <Badge variant="secondary">Niski</Badge>;
      default:
        return <Badge variant="outline">{severity}</Badge>;
    }
  };

  const anomalyTypeLabel: Record<string, string> = {
    MISSING_WORK_END: 'Brak WORK_END',
    WORK_OVER_24H: 'Praca > 24h',
    LONG_WORK: 'Długa praca (>12h)',
    MISSING_BREAK_END: 'Brak BREAK_END',
    MISSING_SMOKE_END: 'Brak SMOKE_END',
    LATE_ARRIVAL: 'Spóźnienie',
    EARLY_DEPARTURE: 'Wcześniejsze wyjście',
    SHORT_BREAK: 'Krótka przerwa',
    SHORT_SMOKE: 'Krótki papierosa',
    LONG_SMOKE: 'Długi papieros',
    WEEKEND_HOLIDAY_WORK: 'Praca w weekend/święto',
    LONG_SHIFT: 'Zmiana >10h',
    UNKNOWN: 'Inne',
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
  };

  const handleAcknowledge = async () => {
    if (!ackTarget) return;
    try {
      await acknowledgeAnomaly({
        variables: {
          employeeId: ackTarget.employeeId,
          date: ackTarget.date,
          anomalyType: ackTarget.type,
          note: ackNote || undefined,
        },
      });
      setAckDialogOpen(false);
      setAckTarget(null);
      setAckNote('');
      refetchAnomalies();
    } catch (e) {
      console.error('Error acknowledging anomaly:', e);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await revokeAcknowledgement({ variables: { id } });
      refetchAnomalies();
    } catch (e) {
      console.error('Error revoking acknowledgement:', e);
    }
  };

  const handleBulkAcknowledge = async () => {
    setBulkAckLoading(true);
    try {
      const unacknowledged = flatAnomalies.filter((_, idx) => selectedAnomalies.has(idx) && !_.acknowledged);
      for (const row of unacknowledged) {
        await acknowledgeAnomaly({
          variables: {
            employeeId: row.employeeId,
            date: row.date,
            anomalyType: row.type,
            note: bulkAckNote || undefined,
          },
        });
      }
      setBulkAckDialogOpen(false);
      setBulkAckNote('');
      setSelectedAnomalies(new Set());
      refetchAnomalies();
    } catch (e) {
      console.error('Error bulk acknowledging anomalies:', e);
    } finally {
      setBulkAckLoading(false);
    }
  };

  // Count selectable (unacknowledged) anomalies
  const selectableCount = flatAnomalies.filter(r => !r.acknowledged).length;
  const selectedUnacknowledgedCount = flatAnomalies.filter((r, idx) => selectedAnomalies.has(idx) && !r.acknowledged).length;

  const toggleSelectAll = () => {
    if (selectedAnomalies.size === selectableCount && selectableCount > 0) {
      setSelectedAnomalies(new Set());
    } else {
      const all = new Set<number>();
      flatAnomalies.forEach((r, idx) => {
        if (!r.acknowledged) all.add(idx);
      });
      setSelectedAnomalies(all);
    }
  };

  const toggleSelect = (idx: number) => {
    setSelectedAnomalies(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  if (loading && activeTab === 'report') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Ładowanie raportu...</div>
      </div>
    );
  }

  if (error && activeTab === 'report') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-500">Błąd podczas ładowania raportu: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {exportMessage && (
        <div className={`p-4 rounded-lg ${exportMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {exportMessage.text}
        </div>
      )}
      {/* Anomaly alert banner — always visible when anomalies exist */}
      {totalAnomalyCount > 0 && activeTab !== 'anomalies' && (
        <div
          className={`p-4 rounded-lg border flex items-center justify-between cursor-pointer transition-colors ${
            highAnomalyCount > 0
              ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950/30 dark:border-red-800 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-950/50'
              : 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-950/30 dark:border-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-950/50'
          }`}
          onClick={() => setActiveTab('anomalies')}
        >
          <div className="flex items-center space-x-3">
            <AlertTriangle className={`w-5 h-5 ${highAnomalyCount > 0 ? 'text-red-600' : 'text-yellow-600'}`} />
            <div>
              <span className="font-medium">
                Wykryto {totalAnomalyCount} {totalAnomalyCount === 1 ? 'anomalię' : totalAnomalyCount < 5 ? 'anomalie' : 'anomalii'}
              </span>
              <span className="text-sm ml-2 opacity-75">
                u {anomalyEmployeeCount} {anomalyEmployeeCount === 1 ? 'pracownika' : 'pracowników'}
                {highAnomalyCount > 0 && (
                  <span className="ml-1">
                    ({highAnomalyCount} {highAnomalyCount === 1 ? 'krytyczna' : highAnomalyCount < 5 ? 'krytyczne' : 'krytycznych'}
                    {mediumAnomalyCount > 0 && `, ${mediumAnomalyCount} średnich`})
                  </span>
                )}
                {highAnomalyCount === 0 && mediumAnomalyCount > 0 && (
                  <span className="ml-1">({mediumAnomalyCount} średnich)</span>
                )}
              </span>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="shrink-0">
            Pokaż szczegóły
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Raporty czasu pracy</h2>
          <p className="text-muted-foreground">Analiza czasu pracy pracowników</p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant={reportPeriod === 'current' ? 'default' : 'outline'}
            onClick={() => handlePeriodChange('current')}
          >
            Bieżący miesiąc
          </Button>
          <Button
            variant={reportPeriod === 'previous' ? 'default' : 'outline'}
            onClick={() => handlePeriodChange('previous')}
          >
            Poprzedni miesiąc
          </Button>
          <Button
            variant={reportPeriod === 'custom' ? 'default' : 'outline'}
            onClick={() => handlePeriodChange('custom')}
          >
            Niestandardowy
          </Button>
        </div>
      </div>

      {/* Date Range Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CalendarDays className="w-5 h-5" />
            <span>Wybór okresu</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div>
              <Label htmlFor="startDate">Data początkowa</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="endDate">Data końcowa</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="departmentFilter">Dział</Label>
              <MultiSelect
                id="departmentFilter"
                options={departments.map((dept: string) => ({ value: dept, label: dept }))}
                selected={selectedDepartments}
                onChange={setSelectedDepartments}
                placeholder="Wszystkie działy"
              />
            </div>
            <div>
              <Label htmlFor="employeeFilter">Pracownik</Label>
              <MultiSelect
                id="employeeFilter"
                options={filteredEmployees.map((emp: any) => ({
                  value: emp.id,
                  label: `${emp.lastName} ${emp.firstName}`,
                }))}
                selected={selectedEmployeeIds}
                onChange={setSelectedEmployeeIds}
                placeholder="Wszyscy pracownicy"
                searchable
                searchPlaceholder="Szukaj..."
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={applyTolerances}
                  onChange={(e) => setApplyTolerances(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-medium">Uwzględnij marginesy</span>
                <Tooltip
                  content={
                    <div className="max-w-xs">
                      Wcześniejsze wejścia i późniejsze wyjścia w ramach marginesów tolerancji
                      nie będą wliczane do czasu pracy (czas rozliczeniowy zamiast rzeczywistego)
                    </div>
                  }
                  side="top"
                >
                  <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                </Tooltip>
              </label>
            </div>
            <div className="flex items-end">
              <Button onClick={handleRefresh} className="w-full">
                Odśwież raport
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab selector */}
      <div className="flex space-x-1 border-b">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'report'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('report')}
        >
          Raport czasu pracy
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center space-x-2 ${
            activeTab === 'anomalies'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('anomalies')}
        >
          <AlertTriangle className="w-4 h-4" />
          <span>Anomalie</span>
          {totalAnomalyCount > 0 && (
            <Badge variant="destructive" className={`ml-1 text-xs ${highAnomalyCount > 0 ? 'animate-pulse' : ''}`}>
              {totalAnomalyCount}
            </Badge>
          )}
        </button>
      </div>

      {activeTab === 'report' && (
        <>
          {/* Summary Cards */}
          {reportData.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Łączny czas pracy</p>
                      <p className="text-2xl font-bold">{formatHours(getTotalHours(reportData, 'work'))}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Coffee className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Łączny czas przerw</p>
                      <p className="text-2xl font-bold">{formatHours(getTotalHours(reportData, 'break'))}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Cigarette className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Łączny czas palenia</p>
                      <p className="text-2xl font-bold">{formatHours(getTotalHours(reportData, 'smoke'))}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Detailed Report Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Szczegółowy raport</CardTitle>
              <div ref={exportDropdownRef} className="relative">
                <Button
                  variant="outline"
                  className="flex items-center space-x-2"
                  onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                  disabled={exportLoading || exportDailyLoading || exportShiftLoading}
                >
                  <Download className="w-4 h-4" />
                  <span>{(exportLoading || exportDailyLoading || exportShiftLoading) ? 'Eksportowanie...' : 'Eksportuj'}</span>
                  <ChevronDown className="w-4 h-4 opacity-50" />
                </Button>
                {exportDropdownOpen && (
                  <div className="absolute right-0 z-50 mt-1 w-56 rounded-md border bg-popover p-1 shadow-md">
                    <button
                      type="button"
                      disabled={exportLoading || reportData.length === 0}
                      onClick={() => { setExportDropdownOpen(false); handleExportToExcel(); }}
                      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      Raport miesięczny
                    </button>
                    <button
                      type="button"
                      disabled={exportDailyLoading || !startDate || !endDate}
                      onClick={() => { setExportDropdownOpen(false); handleExportDailyToExcel(); }}
                      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      Raport dzienny (kadry)
                    </button>
                    <button
                      type="button"
                      disabled={exportShiftLoading || !startDate || !endDate}
                      onClick={() => { setExportDropdownOpen(false); handleExportShiftToExcel(); }}
                      className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileSpreadsheet className="w-4 h-4" />
                      Raport zmianowy (godziny)
                    </button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {reportData.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Brak danych dla wybranego okresu</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Pracownik</th>
                        <th className="text-left p-3">Dział</th>
                        <th className="text-left p-3">Stanowisko</th>
                        <th className="text-center p-3">Czas pracy</th>
                        {applyTolerances && <th className="text-center p-3">Nadgodziny</th>}
                        <th className="text-center p-3">Przerwy</th>
                        <th className="text-center p-3">Papierosy</th>
                        <th className="text-center p-3">Zdarzenia</th>
                        <th className="text-center p-3">Nieobecności</th>
                        <th className="text-center p-3">Anomalie</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((entry: any) => (
                        <tr
                          key={entry.employee.id}
                          className="border-b hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                          onClick={(e) => handleEmployeeClick(entry.employee.id, e)}
                        >
                          <td className="p-3">
                            <div className="font-medium">
                              {entry.employee.lastName} {entry.employee.firstName}
                            </div>
                          </td>
                          <td className="p-3">
                            {entry.employee.department ? (
                              <Badge variant="secondary">{entry.employee.department}</Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            {entry.employee.position || (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="text-center p-3">
                            <div className="flex items-center justify-center space-x-1">
                              <Clock className="w-4 h-4 text-green-600" />
                              <span className="font-medium">{formatHours(entry.workHours)}</span>
                            </div>
                          </td>
                          {applyTolerances && (
                            <td className="text-center p-3">
                              <div className="flex flex-col items-center space-y-1">
                                {entry.overtime50 > 0 && (
                                  <Badge variant="secondary" className="text-xs">
                                    50%: {formatHours(entry.overtime50)}
                                  </Badge>
                                )}
                                {entry.overtime100 > 0 && (
                                  <Badge variant="destructive" className="text-xs">
                                    100%: {formatHours(entry.overtime100)}
                                  </Badge>
                                )}
                                {entry.overtime50 === 0 && entry.overtime100 === 0 && (
                                  <span className="text-muted-foreground text-sm">-</span>
                                )}
                              </div>
                            </td>
                          )}
                          <td className="text-center p-3">
                            <div className="flex items-center justify-center space-x-1">
                              <Coffee className="w-4 h-4 text-orange-600" />
                              <span className="font-medium">{formatHours(entry.breakHours)}</span>
                            </div>
                          </td>
                          <td className="text-center p-3">
                            <div className="flex items-center justify-center space-x-1">
                              <Cigarette className="w-4 h-4 text-gray-600" />
                              <span className="font-medium">{formatHours(entry.smokeHours)}</span>
                            </div>
                          </td>
                          <td className="text-center p-3">
                            <Badge variant="outline">{entry.totalEvents}</Badge>
                          </td>
                          <td className="text-center p-3">
                            {entry.leaveDetails && entry.leaveDetails.length > 0 ? (
                              <div className="flex flex-col items-center space-y-1">
                                {entry.leaveDetails.map((ld: LeaveDetail, idx: number) => (
                                  <Badge
                                    key={idx}
                                    className="text-xs"
                                    style={{
                                      backgroundColor: ld.color,
                                      color: '#fff',
                                    }}
                                  >
                                    {ld.displayName}: {ld.days}d
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </td>
                          <td className="text-center p-3">
                            {(() => {
                              const ac = anomalyByEmployee.get(entry.employee.id);
                              if (!ac || ac.total === 0) return <span className="text-muted-foreground text-sm">-</span>;
                              return (
                                <div
                                  className="flex items-center justify-center space-x-1 cursor-pointer"
                                  onClick={(e) => { e.stopPropagation(); setActiveTab('anomalies'); }}
                                >
                                  {ac.high > 0 && (
                                    <Badge variant="destructive" className="text-xs">{ac.high}</Badge>
                                  )}
                                  {ac.medium > 0 && (
                                    <Badge className="bg-yellow-500 text-white hover:bg-yellow-600 text-xs">{ac.medium}</Badge>
                                  )}
                                  {ac.low > 0 && (
                                    <Badge variant="secondary" className="text-xs">{ac.low}</Badge>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {reportData.length > 0 && (() => {
                    const totalPages = Math.ceil(reportData.length / PAGE_SIZE);
                    return (
                    <div className="flex items-center justify-between pt-4 border-t mt-4">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm text-muted-foreground">
                          Wierszy na stronę
                        </p>
                        <Select
                          value={`${PAGE_SIZE}`}
                          onValueChange={(value) => {
                            const newSize = Number(value);
                            savePageSize(newSize);
                            setCurrentPage(1);
                          }}
                        >
                          <SelectTrigger className="h-8 w-[70px]">
                            <SelectValue placeholder={PAGE_SIZE} />
                          </SelectTrigger>
                          <SelectContent side="top">
                            {[25, 50, 100, 200].map((size) => (
                              <SelectItem key={size} value={`${size}`}>
                                {size}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center space-x-6 lg:space-x-8">
                        <p className="text-sm font-medium">
                          Strona {currentPage} z {totalPages}
                        </p>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            className="hidden h-8 w-8 p-0 lg:flex"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(1)}
                          >
                            <span className="sr-only">Pierwsza strona</span>
                            <ChevronsLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            className="h-8 w-8 p-0"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                          >
                            <span className="sr-only">Poprzednia strona</span>
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            className="h-8 w-8 p-0"
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage(p => p + 1)}
                          >
                            <span className="sr-only">Następna strona</span>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            className="hidden h-8 w-8 p-0 lg:flex"
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage(totalPages)}
                          >
                            <span className="sr-only">Ostatnia strona</span>
                            <ChevronsRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Acknowledge dialog */}
      {ackDialogOpen && ackTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setAckDialogOpen(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Potwierdź anomalię</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Pracownik: <strong>{ackTarget.employeeName}</strong><br />
              Data: {formatDate(ackTarget.date)}<br />
              Typ: {anomalyTypeLabel[ackTarget.type] || ackTarget.type}
            </p>
            <div className="mb-4">
              <Label htmlFor="ackNote">Komentarz (opcjonalny)</Label>
              <Input
                id="ackNote"
                value={ackNote}
                onChange={(e) => setAckNote(e.target.value)}
                placeholder="np. Zweryfikowano z pracownikiem..."
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setAckDialogOpen(false)}>Anuluj</Button>
              <Button onClick={handleAcknowledge}>
                <CheckCircle className="w-4 h-4 mr-1" />
                Potwierdź
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk acknowledge dialog */}
      {bulkAckDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setBulkAckDialogOpen(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Potwierdź {selectedUnacknowledgedCount} {selectedUnacknowledgedCount === 1 ? 'anomalię' : selectedUnacknowledgedCount < 5 ? 'anomalie' : 'anomalii'}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Wszystkie zaznaczone anomalie zostaną potwierdzone.
            </p>
            <div className="mb-4">
              <Label htmlFor="bulkAckNote">Komentarz (opcjonalny)</Label>
              <Input
                id="bulkAckNote"
                value={bulkAckNote}
                onChange={(e) => setBulkAckNote(e.target.value)}
                placeholder="np. Zweryfikowano zbiorczo..."
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setBulkAckDialogOpen(false)} disabled={bulkAckLoading}>Anuluj</Button>
              <Button onClick={handleBulkAcknowledge} disabled={bulkAckLoading}>
                <CheckCircle className="w-4 h-4 mr-1" />
                {bulkAckLoading ? 'Potwierdzanie...' : 'Potwierdź wszystkie'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'anomalies' && (
        <>
          {/* Anomaly summary cards */}
          {anomaliesData?.workAnomalies && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Łącznie anomalii</p>
                      <p className="text-2xl font-bold">{flatAnomalies.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Wysoki priorytet</p>
                      <p className="text-2xl font-bold text-red-600">
                        {(anomaliesData.workAnomalies as AnomalyReport[]).reduce((s, r) => s + r.highCount, 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Średni priorytet</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {(anomaliesData.workAnomalies as AnomalyReport[]).reduce((s, r) => s + r.mediumCount, 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <User className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Pracowników z anomaliami</p>
                      <p className="text-2xl font-bold">{(anomaliesData.workAnomalies as AnomalyReport[]).length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Anomaly filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-4">
                <div className="w-48">
                  <Label>Ważność</Label>
                  <Select value={severityFilter} onValueChange={setSeverityFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Wszystkie</SelectItem>
                      <SelectItem value="HIGH">Wysoki</SelectItem>
                      <SelectItem value="MEDIUM">Średni</SelectItem>
                      <SelectItem value="LOW">Niski</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-56">
                  <Label>Typ anomalii</Label>
                  <Select value={anomalyTypeFilter} onValueChange={setAnomalyTypeFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Wszystkie typy</SelectItem>
                      {anomalyTypes.map((t) => (
                        <SelectItem key={t} value={t}>
                          {anomalyTypeLabel[t] || t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showAcknowledged}
                      onChange={(e) => setShowAcknowledged(e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium">Pokaż potwierdzone</span>
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Anomaly table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5" />
                <span>Wykryte anomalie</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {anomaliesLoading ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Ładowanie anomalii...</p>
                </div>
              ) : flatAnomalies.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Brak anomalii dla wybranego okresu i filtrów</p>
                </div>
              ) : (
                <>
                {selectedUnacknowledgedCount > 0 && (
                  <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Zaznaczono {selectedUnacknowledgedCount} {selectedUnacknowledgedCount === 1 ? 'anomalię' : selectedUnacknowledgedCount < 5 ? 'anomalie' : 'anomalii'}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => { setBulkAckNote(''); setBulkAckDialogOpen(true); }}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Potwierdź zaznaczone
                    </Button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="p-3 w-10">
                          <input
                            type="checkbox"
                            checked={selectableCount > 0 && selectedAnomalies.size === selectableCount}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            disabled={selectableCount === 0}
                          />
                        </th>
                        <th className="text-left p-3">Pracownik</th>
                        <th className="text-left p-3">Data</th>
                        <th className="text-left p-3">Typ</th>
                        <th className="text-center p-3">Ważność</th>
                        <th className="text-left p-3">Opis</th>
                        <th className="text-center p-3">Akcja</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flatAnomalies.map((row, idx) => (
                        <tr
                          key={idx}
                          className={`border-b transition-colors ${
                            row.acknowledged
                              ? 'opacity-50'
                              : row.severity === 'HIGH'
                              ? 'bg-red-50 dark:bg-red-950/20'
                              : row.severity === 'MEDIUM'
                              ? 'bg-yellow-50 dark:bg-yellow-950/20'
                              : ''
                          }`}
                        >
                          <td className="p-3">
                            {!row.acknowledged ? (
                              <input
                                type="checkbox"
                                checked={selectedAnomalies.has(idx)}
                                onChange={() => toggleSelect(idx)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                              />
                            ) : (
                              <span />
                            )}
                          </td>
                          <td className={`p-3 font-medium ${row.acknowledged ? 'line-through text-muted-foreground' : ''}`}>{row.employeeName}</td>
                          <td className={`p-3 ${row.acknowledged ? 'line-through text-muted-foreground' : ''}`}>{formatDate(row.date)}</td>
                          <td className="p-3">
                            <Badge variant="outline" className={`text-xs ${row.acknowledged ? 'opacity-50' : ''}`}>
                              {anomalyTypeLabel[row.type] || row.type}
                            </Badge>
                          </td>
                          <td className="text-center p-3">{severityBadge(row.severity)}</td>
                          <td className={`p-3 text-sm ${row.acknowledged ? 'line-through text-muted-foreground' : ''}`}>{row.message}</td>
                          <td className="text-center p-3">
                            <div className="flex items-center justify-center space-x-1">
                              {row.acknowledged && row.acknowledgement ? (
                                <>
                                  <Tooltip
                                    content={
                                      <div className="max-w-xs text-xs">
                                        <p>Potwierdził: {row.acknowledgement.acknowledgedByEmail}</p>
                                        <p>Data: {new Date(row.acknowledgement.createdAt).toLocaleString('pl-PL')}</p>
                                        {row.acknowledgement.note && <p>Komentarz: {row.acknowledgement.note}</p>}
                                      </div>
                                    }
                                    side="top"
                                  >
                                    <CheckCircle className="w-4 h-4 text-green-600 cursor-help" />
                                  </Tooltip>
                                  {canRevokeAck && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2"
                                      onClick={() => handleRevoke(row.acknowledgement!.id)}
                                    >
                                      <Undo2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 px-2"
                                  onClick={() => {
                                    setAckTarget({ employeeId: row.employeeId, date: row.date, type: row.type, employeeName: row.employeeName });
                                    setAckNote('');
                                    setAckDialogOpen(true);
                                  }}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Potwierdź
                                </Button>
                              )}
                              {onNavigateToEvents && (
                                <Tooltip content="Pokaż wydarzenia pracownika" side="top">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 px-2"
                                    onClick={(e) => {
                                      const d = new Date(row.date);
                                      const from = new Date(d);
                                      from.setDate(from.getDate() - 3);
                                      const to = new Date(d);
                                      to.setDate(to.getDate() + 3);
                                      onNavigateToEvents(row.employeeId, from.toISOString().slice(0, 10), to.toISOString().slice(0, 10), e);
                                    }}
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                  </Button>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
